import { useState, useCallback, ChangeEvent, KeyboardEvent } from "react";
import { processEnvServer } from "hooks/useSsrHooks";
import React, { FC } from "react";
import sanitizeHtml from "sanitize-html";
import { createStyles, makeStyles } from "@mui/styles";
import { getReqContext } from "@RHCommerceDev/utils/reqContext";
import { getSelectorsByUserAgent } from "react-device-detect";

import { useTheme, InputBaseProps } from "@mui/material";
import { useCookiesWithPermission } from "hooks/useCookiesWithPermission";
import { useLocalization } from "hooks/useLocalization";
import {
  FONT_BARON_SANS,
  RH_MEDIUM,
  RH_DARK,
  BCT_PATHS
} from "utils/constants";
import { codePattern } from "utils/regex";
import { formatSearchText } from "utils/formatSearchUrl";
import yn from "yn";
import { useEnv } from "hooks/useEnv";
import { useHistory } from "react-router";
import { useSetSipId } from "@RHCommerceDev/hooks/atoms/useSIPID";
import useSite from "hooks/useSite";
import { useAppId } from "hooks/useAppId";
import RHRSearchIconV2 from "@RHCommerceDev/icon-search-rhr-v2";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import parseUrlPath from "@RHCommerceDev/utils/parse-url-path";
import useIsoRedirect from "@RHCommerceDev/hooks/useIsoRedirect";
import { usePageContent } from "customProviders/LocationProvider";
import { SEARCH_RHCOM } from "@RHCommerceDev/resources/rh-header.json";

export const useStyles = isDarkMode =>
  makeStyles(() =>
    createStyles({
      searchInput: {
        padding: "8px !important",
        marginLeft: "12px !important",
        paddingLeft: "32px !important",
        fontFamily: `${FONT_BARON_SANS} !important`,
        color: `${isDarkMode ? RH_MEDIUM : RH_DARK} !important`
      }
    })
  );

interface HeaderSearchTextFieldProps extends InputBaseProps {
  isDarkMode?: boolean;
  isHeaderSearch?: boolean;
  isHighlighted?: boolean;
  openDrawer: () => void;
}

const HeaderSearchTextField: FC<
  HeaderSearchTextFieldProps & { color?: string }
> = ({ isHeaderSearch, isDarkMode, color = undefined, isHighlighted }) => {
  const siteId = useSite();
  const history = useHistory();
  const theme = useTheme();
  const prefix = useLocalization();
  const { pageContent } = usePageContent();
  const [searchText, setSearchText] = useState<string>("");
  const [showTypeAhead, setShowTypeAhead] = useState(false);
  const { setStorageValueWrapper } = useCookiesWithPermission();
  const env = useEnv();
  const { closeHamburgerDropdown } = useHeaderAtom();
  const req = getReqContext();
  let mobile = false;
  const userAgentSSR = req && req?.headers["user-agent"];
  if (userAgentSSR) {
    const { isMobile } = getSelectorsByUserAgent(userAgentSSR);
    mobile = !!isMobile;
  }
  const setSipId = useSetSipId();
  const { isConcierge } = useAppId();
  const FEATURE_NEW_HEADER = yn(env?.FEATURE_NEW_HEADER);
  const FEATURE_SHOP_UI = yn(env?.FEATURE_SHOP_UI);

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleTypeahead = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!processEnvServer) {
        setStorageValueWrapper({
          storageKey: "analytics-access",
          value: "typeahead"
        });
      }
      setSearchText(event.target.value);
      if (!showTypeAhead && !isHeaderSearch) {
        setShowTypeAhead(true);
      }
    },
    [showTypeAhead, isHeaderSearch, setStorageValueWrapper]
  );

  const handleEnterPressed = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const bctPath =
        yn(env?.FEATURE_BCT_SUNSET) && !isConcierge
          ? BCT_PATHS[siteId] || ""
          : "";
      const searchPath = yn(env?.FEATURE_URL_CHANGE)
        ? formatSearchText(searchText)
        : `results.jsp?Ntt=${encodeURIComponent(
            searchText?.trim()
          )}&Ns=product.sale%7C1`;
      if (
        !!searchText &&
        event.key === "Enter" &&
        !!codePattern.test(searchText)
      ) {
        setSipId(null);

        if (!processEnvServer) {
          sessionStorage.removeItem("pdpViewItem");
          sessionStorage.removeItem("rhrGridChoice");
          setStorageValueWrapper({
            storageKey: "analytics-access",
            value: "direct"
          });
          localStorage.setItem("analytics-searchbar-enter", searchText);
        }
        const path = sanitizeHtml(`${prefix}${bctPath}/search/${searchPath}`);

        if (FEATURE_SHOP_UI) {
          useIsoRedirect(parseUrlPath(path), "windowAssign", {
            history
          });
        } else {
          history.push(path);
        }

        closeHamburgerDropdown();
        setSearchText("");
      }
    },

    [searchText, prefix, setStorageValueWrapper]
  );

  return FEATURE_NEW_HEADER ? (
    <div
      className="relative flex h-[26px] w-[148px] items-center gap-[1px] rounded-3xl border-[0.5px] px-1 py-[3px] pl-7 md:w-[150px] lg:w-[196px]"
      style={{
        borderColor:
          color ||
          (isDarkMode ? theme.palette.common.white : theme.palette.common.black)
      }}
      aria-label={`${pageContent?.globalHeader?.SEARCH_RHCOM ?? SEARCH_RHCOM}`}
    >
      <RHRSearchIconV2
        style={{
          color:
            color ||
            (isDarkMode
              ? theme.palette.common.white
              : theme.palette.common.black)
        }}
        id="search-icon"
        data-testid="search-icon"
        className="absolute left-[2px]"
      />
      <input
        value={searchText}
        className="w-full bg-transparent font-rhsans text-[13px] font-normal leading-[1em] tracking-[0.015em] outline-none placeholder:text-gray-500"
        style={{
          color:
            color ||
            (isDarkMode
              ? theme.palette.common.white
              : theme.palette.common.black),
          caretColor:
            color ||
            (isDarkMode
              ? theme.palette.common.white
              : theme.palette.common.black)
        }}
        onChange={handleTypeahead}
        onKeyUp={handleEnterPressed}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={isFocused ? pageContent?.globalHeader?.SEARCH : ""}
      />
    </div>
  ) : (
    <div className="flex items-center gap-[6px]">
      <button
        className="p-0"
        onClick={() => setIsOpen(prev => !prev)}
        id="search-icon"
      >
        <RHRSearchIconV2
          style={{
            color:
              color ||
              (isDarkMode
                ? theme.palette.common.white
                : theme.palette.common.black)
          }}
          data-testid="search-icon"
          aria-label={`${
            pageContent?.globalHeader?.SEARCH_RHCOM ?? SEARCH_RHCOM
          }`}
        />
      </button>
      {isOpen && (
        <input
          autoFocus
          value={searchText}
          className="rounded-none border-b-[0.75px] border-b-rh-dark bg-transparent font-rhsans text-[13px] font-normal leading-[150%] tracking-[0.195px] caret-rh-dark outline-none sm:w-[120px] lg:w-[140px]"
          style={{
            color:
              color ||
              (isDarkMode
                ? theme.palette.common.white
                : theme.palette.common.black)
          }}
          onChange={handleTypeahead}
          onKeyUp={handleEnterPressed}
          aria-label="Search text"
        />
      )}
    </div>
  );
};

export default HeaderSearchTextField;
